import React, { useState, useEffect } from 'react';
import { FiFolder, FiUsers, FiMail, FiBriefcase } from 'react-icons/fi';
import StatCard from '../../components/ui/StatCard';
import LineChart from '../../components/charts/LineChart';
import { UseToast } from '../../hooks/UseToast';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const Dashboard = () => {
  const toast = UseToast();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState([
    { title: 'Total Projects', value: '0', change: '0%', icon: <FiFolder />, color: '#3B82F6' },
    { title: 'Active Clients', value: '0', change: '0%', icon: <FiUsers />, color: '#10B981' },
    { title: 'Pending Messages', value: '0', change: '0%', icon: <FiMail />, color: '#F59E0B' },
    { title: 'Job Applications', value: '0', change: '0%', icon: <FiBriefcase />, color: '#8B5CF6' },
  ]);
  const [recentProjects, setRecentProjects] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // ✅ Parallel fetch all data
      const [projectsRes, appsRes, messagesRes, serviceMessagesRes] = await Promise.all([
        fetch(`${API_URL}/projects`, { headers }),
        fetch(`${API_URL}/applications`, { headers }),
        fetch(`${API_URL}/service-messages`, { headers }),
        fetch(`${API_URL}/service-messages`, { headers }) // For inquiries chart
      ]);

      const projectsData = await projectsRes.json();
      const appsData = await appsRes.json();
      const messagesData = await messagesRes.json();
      const serviceMessagesData = await serviceMessagesRes.json();

      // ✅ Calculate stats
      const totalProjects = projectsData.success ? projectsData.data?.length || 0 : 0;
      const totalApps = appsData.success ? appsData.data?.length || 0 : 0;
      const newMessages = messagesData.success ? messagesData.data?.filter(m => m.status === 'new').length || 0 : 0;
      
      // ✅ Active clients - unique from projects
      const activeClients = projectsData.success 
        ? [...new Set(projectsData.data?.map(p => p.client).filter(Boolean))].length 
        : 0;

      setStats([
        { 
          title: 'Total Projects', 
          value: totalProjects.toString(), 
          change: '+12%', 
          icon: <FiFolder />, 
          color: '#3B82F6' 
        },
        { 
          title: 'Active Clients', 
          value: activeClients.toString(), 
          change: '+8%', 
          icon: <FiUsers />, 
          color: '#10B981' 
        },
        { 
          title: 'Pending Messages', 
          value: newMessages.toString(), 
          change: newMessages > 0 ? '+5%' : '0%', 
          icon: <FiMail />, 
          color: '#F59E0B' 
        },
        { 
          title: 'Job Applications', 
          value: totalApps.toString(), 
          change: '+15%', 
          icon: <FiBriefcase />, 
          color: '#8B5CF6' 
        },
      ]);

      // ✅ Recent projects - last 5
      if (projectsData.success) {
        const sorted = projectsData.data
          ?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 5)
          .map(p => ({
            id: p._id,
            name: p.title,
            category: p.category,
            status: p.status === 'completed' ? 'Completed' : 'In Progress',
            date: new Date(p.createdAt).toLocaleDateString('en-IN')
          }));
        setRecentProjects(sorted || []);
      }

      // ✅ Monthly inquiries for chart
      if (serviceMessagesData.success) {
        const monthlyCount = Array(12).fill(0);
        serviceMessagesData.data?.forEach(msg => {
          const month = new Date(msg.createdAt).getMonth();
          monthlyCount[month]++;
        });
        setMonthlyData(monthlyCount);
      }

    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast.error('Failed to load dashboard data');
    }
    setLoading(false);
  };

  return (
    <>
      <style>{`
.dashboard { }
.page-header { margin-bottom: 30px; }
.page-title { font-size: 32px; font-weight: 800; color: #0F172A; margin: 0 0 8px 0; }
.page-subtitle { color: #64748B; font-size: 15px; }
.stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 24px; margin-bottom: 30px; }
.chart-section,.table-card { background: #FFF; border-radius: 16px; padding: 30px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); margin-bottom: 30px; }
.section-title { font-size: 20px; font-weight: 700; color: #0F172A; margin: 0 0 20px 0; }
.data-table { width: 100%; border-collapse: collapse; }
.data-table th { text-align: left; padding: 12px; font-size: 13px; font-weight: 700; color: #64748B; text-transform: uppercase; border-bottom: 2px solid #E2E8F0; }
.data-table td { padding: 16px 12px; border-bottom: 1px solid #F1F5F9; font-size: 14px; color: #334155; }
.data-table tr:hover { background: #F8FAFC; }
.status-badge { padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; display: inline-block; }
.status-completed { background: #D1FAE5; color: #065F46; }
.status-progress { background: #DBEAFE; color: #1E40AF; }
.loading-state { text-align: center; padding: 40px; color: #64748B; }
      `}</style>
      <div className="dashboard">
        <div className="page-header">
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Welcome back, Admin! Here's what's happening today.</p>
        </div>
        
        {loading ? (
          <div className="loading-state">Loading dashboard...</div>
        ) : (
          <>
            <div className="stats-grid">
              {stats.map((stat, idx) => <StatCard key={idx} {...stat} />)}
            </div>
            
            <div className="chart-section">
              <h2 className="section-title">Monthly Inquiries</h2>
              <LineChart data={monthlyData} />
            </div>
            
            <div className="table-card">
              <h2 className="section-title">Recent Projects</h2>
              {recentProjects.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748B' }}>
                  No projects yet
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Project Name</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentProjects.map((p) => (
                      <tr key={p.id}>
                        <td><strong>{p.name}</strong></td>
                        <td>{p.category}</td>
                        <td>
                          <span className={`status-badge ${p.status === 'Completed' ? 'status-completed' : 'status-progress'}`}>
                            {p.status}
                          </span>
                        </td>
                        <td>{p.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Dashboard;